import {graphql} from 'react-relay';
import {buildTicketFilters, type TicketFiltersInput} from '../../tickets/hooks/useTicketListQuery';
import type {DashboardFilterState} from './useDashboardFilterPanel';
import type {DashboardDateField} from './useDashboardFilters';

/**
 * Rows per page, and the increment each "Load more" adds. Shared by the route's
 * query variables and the pagination hook so the first page and every later one
 * are the same size.
 */
export const DASHBOARD_TICKET_PAGE_SIZE = 50;

export const DashboardTicketsQuery = graphql`
	query useDashboardTicketsQueryQuery($first: Int, $after: String, $filters: TicketFilters) {
		viewer {
			actualPersonId
			...useDashboardTicketsQuery_tickets @arguments(first: $first, after: $after, filters: $filters)
		}
	}
`;

export const DashboardTicketsPaginationFragment = graphql`
	fragment useDashboardTicketsQuery_tickets on Viewer
	@refetchable(queryName: "useDashboardTicketsQueryPaginationQuery")
	@argumentDefinitions(first: {type: "Int", defaultValue: 50}, after: {type: "String"}, filters: {type: "TicketFilters"}) {
		tickets(first: $first, after: $after, filters: $filters) @connection(key: "DashboardTickets_tickets") {
			edges {
				cursor
				node {
					id
					subject
					status
					priority
					channel
					slug
					requesterId
					assigneeId
					createdAt
					resolvedAt
					lastActivityAt
					resolutionTimeSeconds
					firstResponseTimeSeconds
					requester {
						id
						name
						email
					}
					assignee {
						fullName
					}
					labels {
						id
						name
						color
					}
					sla {
						breached
						elapsedSeconds
						thresholdSeconds
						paused
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

/**
 * Maps the dashboard's filter-panel state and toolbar date range into the
 * shape {@link buildTicketFilters} expects, then calls it to produce the
 * GraphQL-compatible filter object.
 *
 * `groupBy` is deliberately not included — changing the grouping re-lays-out
 * loaded rows client-side and must never cause a refetch.
 */
export function buildDashboardTicketFilters(
	applied: DashboardFilterState,
	from: string,
	to: string,
	dateField: DashboardDateField
): Record<string, unknown> | undefined {
	let created_after = '';
	let created_before = '';
	let updated_after = '';
	let updated_before = '';

	if (dateField === 'last_activity_at') {
		updated_after = from;
		updated_before = to;
	} else {
		// 'created_at' maps directly to created-date range.
		// 'resolved_at' has no corresponding backend filter input yet; falls back to
		// created-date range as a temporary approximation until the backend exposes a
		// resolved-date range input.
		created_after = from;
		created_before = to;
	}

	const filtersInput: TicketFiltersInput = {
		status: applied.status,
		priority: applied.priority,
		sla_status: applied.slaState,
		assignee_id: applied.assignee,
		label_id: applied.tags,
		requester_id: applied.requester,
		created_after,
		created_before,
		updated_after,
		updated_before,
	};

	const base = buildTicketFilters(filtersInput, undefined, undefined, undefined);
	const result: Record<string, unknown> = base ? {...base} : {};

	if (applied.channel.length > 0) {
		result.channel = applied.channel;
	}

	return Object.keys(result).length > 0 ? result : undefined;
}
